import React, { createContext, useContext, useState, useCallback } from "react";
import {
  PointsActionCodeRedemption,
  createPointsAction,
  PointsActionCodeActivation,
} from "@t3/shared";
import { collection, doc, setDoc } from "firebase/firestore";
import { firestore } from "@/firebase";
import { useAuthContext } from "@/context/AuthContext";
import { useProgramContext } from "@/context/ProgramContext";
import { pointsActionConverter } from "@/converters";

interface CodeContextType {
  submitError: string | null;
  loadingRedeemCode: boolean;
  submitLoading: boolean;
  redeemCode: (codeIdOrValue: string) => Promise<boolean>;
  cancelSubmit: () => void;
  createCode: (points: number, code: string) => Promise<boolean>;
}

const CodeContext = createContext<CodeContextType | undefined>(undefined);

export function RedemptionCodeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuthContext();
  const { program } = useProgramContext();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingRedeemCode, setLoadingRedeemCode] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const redeemCode = useCallback(
    async (codeId: string) => {
      try {
        setLoadingRedeemCode(true);
        const pointsActionsCollection = doc(
          collection(firestore, `company/${user.company}/points-actions`),
        ).withConverter(pointsActionConverter);

        const pointsAction = createPointsAction<PointsActionCodeRedemption>({
          type: "codeRedemption",
          status: "submitted",
          typeMeta: {
            codeId: codeId,
            points: null,
            userBeginningBalance: null,
            userEndingBalance: null,
          },
        });

        await setDoc(pointsActionsCollection, pointsAction);
        return true;
      } catch (error) {
        console.error("Error redeeming code:", error);
        throw error;
      } finally {
        setLoadingRedeemCode(false);
      }
    },
    [user?.company, user?.id, program?.id],
  );

  const createCode = useCallback(
    async (points: number, code: string) => {
      if (user?.role === "super-admin") {
        try {
          // Fixed: Generate a unique document ID first
          const pointsActionsCollection = doc(
            collection(firestore, `company/${user.company}/points-actions`),
          ).withConverter(pointsActionConverter);

          const pointsAction = createPointsAction<PointsActionCodeActivation>({
            type: "codeActivation",
            status: "submitted",
            typeMeta: {
              codeId: code,
              programId: program.id,
              points,
              pointsBucketId: null,
              featureType: "raves",
            },
          });
          console.log("pointsAction", pointsAction);
          await setDoc(pointsActionsCollection, pointsAction);
          return true;
        } catch (error) {
          console.error("Error creating code:", error);
          return false;
        }
      }
      return false;
    },
    [user?.company, user?.id],
  );

  const cancelSubmit = useCallback(() => {
    setSubmitError(null);
    setSubmitLoading(false);
  }, []);

  const value = {
    submitError,
    loadingRedeemCode,
    submitLoading,
    redeemCode,
    cancelSubmit,
    createCode,
  };

  return <CodeContext.Provider value={value}>{children}</CodeContext.Provider>;
}

export function useRedemptionCodeContext() {
  const context = useContext(CodeContext);
  if (context === undefined) {
    throw new Error("useCodes must be used within a CodeProvider");
  }
  return context;
}
